import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  MarketingWorkspaceConfigProvider,
  useMarketingWorkspaceConfig,
  type IMarketingWorkspaceConfig,
} from "./config";
import { useMarketingWorkspaces } from "./hooks/useMarketingWorkspaces";
import { useMyPendingInvitations } from "./hooks/useWorkspaceInvitations";
import {
  getActiveWorkspaceId,
  setActiveWorkspaceId,
  subscribeToActiveWorkspace,
} from "./storage";
import type { IMarketingWorkspace, IMarketingWorkspaceInvitation } from "./types";

interface IMarketingWorkspaceContext {
  workspaces: IMarketingWorkspace[];
  activeWorkspace: IMarketingWorkspace | null;
  activeWorkspaceId: string | null;
  pendingInvitations: IMarketingWorkspaceInvitation[];
  hasLoaded: boolean;
  isResolved: boolean;
  isLoading: boolean;
  error?: Error;
  selectWorkspace: (workspaceId: string | null) => void;
  refetchWorkspaces: () => void;
  refetchInvitations: () => void;
}

const WorkspaceContext = createContext<IMarketingWorkspaceContext>({
  workspaces: [],
  activeWorkspace: null,
  activeWorkspaceId: null,
  pendingInvitations: [],
  hasLoaded: false,
  isResolved: false,
  isLoading: true,
  selectWorkspace: () => undefined,
  refetchWorkspaces: () => undefined,
  refetchInvitations: () => undefined,
});

export function MarketingWorkspaceProvider({
  config,
  isAuthenticated,
  children,
}: {
  config: IMarketingWorkspaceConfig;
  isAuthenticated: boolean;
  children: ReactNode;
}) {
  return (
    <MarketingWorkspaceConfigProvider config={config}>
      <WorkspaceState isAuthenticated={isAuthenticated}>{children}</WorkspaceState>
    </MarketingWorkspaceConfigProvider>
  );
}

function WorkspaceState({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: ReactNode;
}) {
  const { mode } = useMarketingWorkspaceConfig();
  const { workspaces, hasLoaded, isLoading, error, refetch } =
    useMarketingWorkspaces(!isAuthenticated);

  const invitationsSkipped = !isAuthenticated || mode !== "multi";

  const {
    invitations: pendingInvitations,
    hasLoaded: hasLoadedInvitations,
    error: invitationsError,
    refetch: refetchPendingInvitations,
  } = useMyPendingInvitations(invitationsSkipped);

  const areInvitationsResolved =
    invitationsSkipped || hasLoadedInvitations || Boolean(invitationsError);

  const isResolved = (hasLoaded || Boolean(error)) && areInvitationsResolved;

  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<string | null>(
    getActiveWorkspaceId
  );
  useEffect(() => subscribeToActiveWorkspace(setActiveWorkspaceIdState), []);

  useEffect(() => {
    if (!hasLoaded) return;

    const isStillAMember = workspaces.some((workspace) => workspace.id === activeWorkspaceId);

    if (activeWorkspaceId && !isStillAMember) {
      setActiveWorkspaceId(null);
      return;
    }

    // Nothing is ever created here: a user who belongs to no workspace simply has none. A multi
    // host sends them to its picker / create screens, a single host shows them an empty state. The
    // only automatic choice is the first membership, when it is the only one or the host is single.
    const defaultWorkspace =
      workspaces.length === 1 || mode === "single" ? workspaces[0] : undefined;

    if (!activeWorkspaceId && defaultWorkspace) {
      setActiveWorkspaceId(defaultWorkspace.id);
    }
  }, [mode, hasLoaded, workspaces, activeWorkspaceId]);

  const selectWorkspace = useCallback((workspaceId: string | null) => {
    setActiveWorkspaceId(workspaceId);
  }, []);

  const value = useMemo<IMarketingWorkspaceContext>(
    () => ({
      workspaces,
      activeWorkspace:
        workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
      activeWorkspaceId,
      pendingInvitations,
      hasLoaded,
      isResolved,
      isLoading,
      error: error as Error | undefined,
      selectWorkspace,
      refetchWorkspaces: () => {
        void refetch();
      },
      refetchInvitations: () => {
        void refetchPendingInvitations();
      },
    }),
    [
      workspaces,
      activeWorkspaceId,
      pendingInvitations,
      hasLoaded,
      isResolved,
      isLoading,
      error,
      selectWorkspace,
      refetch,
      refetchPendingInvitations,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useMarketingWorkspace() {
  return useContext(WorkspaceContext);
}
